/**
 * @coeldery/page-flip — **翻頁容器**（左右翻；日記簿用）
 *
 * 為何係模組（R3）：任何「一頁一頁睇」嘅介面（日記／相簿／每日回顧／卡片）都要同一套手勢同鍵盤操作
 *   （滑動 ＋ 箭嘴掣 ＋ ←／→），重複手寫一定各處唔一致。查庫證據（2026-09-22）：
 *   `diary`／`flip`／`swipe` 0 命中；`gen-carousel` 係 `product:coeldery-family-tree` **產品專屬**（連
 *   `household-card`，唔可以跨產品用）→ 依 R3 新建。
 *
 * 血緣：cosingery（2026-09-22 產品負責人：「it should be like a dairy, the format is to flip left to
 *   right and right to left, it should be show as a dairy page」；`decisions/027` 批次 B）。
 *
 * ⚠️ 設計：模組**唔識**產品內容 —— `pages` 逐頁由父層砌（每頁一個 `key`）；模組只管翻頁。
 *   · 手勢：`touchstart`／`touchend` 計水平位移（`flipDirection()` 純函數；斜掃唔算，防誤觸）
 *   · 撳掣〔‹〕〔›〕＋鍵盤 ←／→（同 `flipDirection` 一樣**唔循環**）
 *   · 頁碼由 `pageLabel()` 砌（`3 / 12`）；文字一律由父層傳（唔准寫死產品文案）
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { flipDirection, pageLabel, stepPage } from './flip.ts';
import FlipSheet from './sheet.tsx';

export { clampPage, flipDirection, pageLabel, stepPage } from './flip.ts';

/** `prefers-reduced-motion: reduce` → 唔動畫（無障礙；同 `app.css` 一致） */
function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

export interface FlipPage {
  /** 穩定 key（通常係 id） */
  key: string;
  node: ReactNode;
}

export interface PageFlipProps {
  pages: FlipPage[];
  index: number;
  onIndexChange: (index: number) => void;
  /** 無障礙名稱（i18n key） */
  labelKey?: string;
  /** 上一頁／下一頁嘅無障礙文字（i18n key） */
  prevKey?: string;
  nextKey?: string;
  busy?: boolean;
  /**
   * ⭐ 1.1.0：**要唔要模組自己嘅底部控制**（〔‹〕〔›〕＋頁碼）
   *   · `true`（預設）＝ 顯示（舊行為）
   *   · `false` ＝ **唔顯示**（例：翻頁掣搬去頂欄 —— 產品負責人 2026-09-22 指定）
   *   ⚠️ 無論邊個 mode，**滑動同鍵盤 ←／→ 都照樣有效**（模組仍然管手勢）
   */
  chrome?: boolean;
  /**
   * ⭐ 1.2.0（產品負責人 2026-09-24：「see if the presentation is more like reading a diary. from visually
   *   and the flipping action, so be more obvious」）：**跟手揭頁 ＋ 3D 頁轉 ＋ 頁角提示**
   *   · `true`（預設）＝ 顯示頁角提示（「掀返之前／掀去之後」）
   *   · `false` ＝ 唔顯示（仍然可以滑／撳掣／鍵盤）
   */
  edgeHints?: boolean;
  /** ⭐ 1.2.0：頁角提示文字（i18n key） */
  hintPrevKey?: string;
  hintNextKey?: string;
}

export default function PageFlip({
  pages,
  index,
  onIndexChange,
  labelKey,
  prevKey = 'diary_prev',
  nextKey = 'diary_next',
  busy = false,
  chrome = true,
  edgeHints = true,
  hintPrevKey = 'flip_hint_prev',
  hintNextKey = 'flip_hint_next',
}: PageFlipProps) {
  const { t } = useTranslation();
  const [drag, setDrag] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const current = stepPage(pages.length, index, 0);
  const page = current >= 0 ? pages[current] : null;
  const canPrev = current > 0;
  const canNext = current >= 0 && current < pages.length - 1;
  const reduce = useReducedMotion();
  /** ⭐ 1.3.0：最近一次掀嘅方向（>0 ＝ 掀去之後；<0 ＝ 掀返之前）→ 驅動新頁入場動畫 */
  const [turn, setTurn] = useState(1);
  /** ⭐ 1.3.0：**底層鄰頁**（跟手時透出 —— 似真書「掀起一頁，下面嗰頁露出嚟」） */
  const peek = drag > 0 ? pages[current - 1] : drag < 0 ? pages[current + 1] : null;
  /** 換頁（統一入口：記低方向 ＋ 通知父層） */
  const go = (dir: number) => {
    const next = stepPage(pages.length, current, dir);
    if (next < 0) return;
    setTurn(dir);
    onIndexChange(next);
  };

  /** 鍵盤 ←／→（桌面用） */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (delta === 0) return;
      const next = stepPage(pages.length, current, delta);
      if (next >= 0) {
        setTurn(delta);
        onIndexChange(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pages.length, current, onIndexChange]);

  const arrowBtn = (enabled: boolean): React.CSSProperties => ({
    minWidth: '44px',
    minHeight: '44px',
    borderRadius: '999px',
    border: '1.5px solid var(--color-divider)',
    background: 'var(--color-card)',
    color: 'var(--color-text)',
    fontFamily: 'inherit',
    fontSize: '18px',
    cursor: enabled && !busy ? 'pointer' : 'default',
    opacity: enabled && !busy ? 1 : 0.35,
  });

  return (
    <div data-testid="page-flip" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div
        role="group"
        aria-label={labelKey ? t(labelKey) : undefined}
        onTouchStart={(e) => {
          const tp = e.touches[0];
          start.current = tp ? { x: tp.clientX, y: tp.clientY } : null;
        }}
        onTouchMove={(e) => {
          const tp = e.touches[0];
          if (!tp || !start.current) return;
          const dx = tp.clientX - start.current.x;
          const dy = tp.clientY - start.current.y;
          /** 斜掃（垂直為主）唔算揭頁 → 唔跟手（否則捲動／選字會亂） */
          if (Math.abs(dy) > Math.abs(dx)) return;
          /**
           * ⭐ 1.2.0：**跟手**（頁面跟住手指走）—— 上限 ±140px（唔會拖到出界），
           *   同時計一個輕微 `rotateY`（3D 揭頁感）。
           * ⭐ 1.3.0：加大到 ±16°（掀得更明顯）＋ 底層鄰頁透出（見下面 render）
           */
          setDrag(Math.max(-160, Math.min(160, dx)));
        }}
        onTouchEnd={(e) => {
          const tp = e.changedTouches[0];
          const from = start.current;
          start.current = null;
          setDrag(0);
          if (!tp || !from) return;
          const dx = tp.clientX - from.x;
          const dy = tp.clientY - from.y;
          /**
           * ⭐ 1.3.0：**短掃都接到** —— 橫掃 ≥ 56px 就當掀頁（原本要 `flipDirection` 判完，
           *   手指郁得少少就冇反應，好易覺得「唔靈」）。斜掃（垂直為主）仍然唔算。
           */
          const dir = Math.abs(dy) > Math.abs(dx) ? 0 : Math.abs(dx) >= 56 ? (dx < 0 ? 1 : -1) : flipDirection(dx, dy);
          if (dir === 0) return;
          go(dir);
        }}
        style={{
          /** 觸控唔准變成瀏覽器「上一頁／下一頁」手勢 */
          touchAction: 'pan-y',
          position: 'relative',
          perspective: '1400px',
        }}
      >
        {/* ⭐ 1.2.0：頁角提示（似揭書）—— 有得掀就顯示 */}
        {edgeHints ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '6px',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-tiny, 13.5px)',
              pointerEvents: 'none',
            }}
          >
            <span data-testid="flip-hint-prev">{canPrev ? `← ${t(hintPrevKey)}` : ''}</span>
            <span data-testid="flip-hint-next">{canNext ? `${t(hintNextKey)} →` : ''}</span>
          </div>
        ) : null}

        {/* ⭐ 1.3.0：書頁紙疊（底層鄰頁／紙感／陰影）＋ 面層跟手頁 —— 實作喺 `sheet.tsx` */}
        <FlipSheet
          drag={drag}
          reduce={reduce}
          turn={turn}
          pageKey={page?.key ?? 'empty'}
          page={page?.node ?? null}
          peekNode={peek?.node ?? null}
          peekOn={Boolean(peek)}
        />
      </div>

      {chrome ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <button
            type="button"
            data-testid="flip-prev"
            aria-label={t(prevKey)}
            disabled={!canPrev || busy}
            onClick={() => go(-1)}
            style={arrowBtn(canPrev)}
          >
            ‹
          </button>
          <span
            data-testid="flip-page-label"
            style={{ fontSize: 'var(--font-size-tiny, 13.5px)', color: 'var(--color-text-secondary)', fontWeight: 700 }}
          >
            {pageLabel(current, pages.length)}
          </span>
          <button
            type="button"
            data-testid="flip-next"
            aria-label={t(nextKey)}
            disabled={!canNext || busy}
            onClick={() => go(1)}
            style={arrowBtn(canNext)}
          >
            ›
          </button>
        </div>
      ) : null}
    </div>
  );
}
